import { CommonModule } from "@angular/common";
import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { filter, Observable, Subject, takeUntil } from "rxjs";
import { LoadingComponent } from "../shared/components/loading/loading.component";
import { AuthService } from "./services/auth.service";
import { LoadingService } from "./services/loading.service";
import { NotificationRealtimeService } from "./services/notification-realtime.service";
import { NotificationService } from "./services/notification.service";
import { NotificationStore } from "./services/notification.store";
import { SnackBarService } from "./services/snack-bar.service";
import { AttributionContextService } from "./services/marketing/attribution-context.service";
import { MarketingPixelService } from "./services/marketing/marketing-pixel.service";

@Component({
  selector: "app-root",
  imports: [RouterOutlet, LoadingComponent, CommonModule],
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.scss",
})
export class AppComponent implements OnInit, OnDestroy {
  title = "yes-seven-ticket";
  isLoading$: Observable<boolean>;

  private readonly authService = inject(AuthService);
  private readonly realtime = inject(NotificationRealtimeService);
  private readonly notificationService = inject(NotificationService);
  private readonly notificationStore = inject(NotificationStore);
  private readonly snackbarService = inject(SnackBarService);
  private readonly marketingContext = inject(AttributionContextService);
  private readonly marketingPixels = inject(MarketingPixelService);
  private readonly router = inject(Router);

  private readonly destroy$ = new Subject<void>();

  constructor(private loadingService: LoadingService) {
    this.isLoading$ = this.loadingService.loading$;
  }

  ngOnInit(): void {
    this.marketingContext.observeNavigation();
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.marketingContext.observeNavigation();
        if (!/^\/event\/\d+(?:[?#]|$)/.test(this.router.url)) {
          this.marketingPixels.leaveEvent();
          this.marketingContext.leaveEvent();
        }
      });
    this.authService.isLoggedIn
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLoggedIn) => {
        if (isLoggedIn) {
          this.startNotifications();
        } else {
          this.stopNotifications();
        }
      });

    this.realtime.newNotification$
      .pipe(takeUntil(this.destroy$))
      .subscribe((notification) => {
        this.snackbarService.openSnackBar(notification.title, "Fechar", "info");
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopNotifications();
  }

  private startNotifications(): void {
    this.realtime.connect();
    this.notificationStore.setLoading(true);
    this.notificationService
      .list(1, 20, false)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => this.notificationStore.setFromList(result));
  }

  private stopNotifications(): void {
    this.realtime.disconnect();
    this.notificationStore.reset();
  }
}
